import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { defaultTo, map, pickBy, keys, capitalize, mapValues } from 'lodash';

import MinimizableItem from 'src/components/DraggableContainer/MinimizableItem';
import MacroSummary from 'src/components/Mfp/MacroSummary/MacroSummary';
import { isRequiredDimension } from 'src/space';
import { SelectedSpace } from 'src/components/AnchorRadioMultiSelect/AnchorCheckboxSelect';
import { Overlay } from 'src/common-ui';

import Grid from '@material-ui/core/Grid';
import './_MacroSummaries.scss';

import { mapDispatchToProps, mapStateToProps } from './MacroSummaries.container';

import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';

export type MacroSummariesProps = typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>;

const MacroSummaries = (props: MacroSummariesProps): JSX.Element => {
  const {
    metrics,
    showMinimizableItem,
    currentAnchors,
    setSelcetedMacroAnchors,
    selectedMacroAnchors,
    anchorMembers,
    setMfpMacroSummariesCollapsed,
    isMfpMacroSummariesCollapsed
  } = props;
  const reallyShowMinimizableItem = defaultTo(showMinimizableItem, true);

  const dimensions = pickBy(currentAnchors, (_value, key) => isRequiredDimension(key));
  const dimensionKeys = keys(dimensions);

  const [dimensionState, setDimensionState] = useState<SelectedSpace | null>(() => {
    if (!selectedMacroAnchors || !anchorMembers || !currentAnchors) return null;
    return mapValues(anchorMembers, (v, dim) => {
      return v.map((m) => {
        const inc = selectedMacroAnchors[dim]?.some((id) => m.id === id);
        return { id: m.id, name: m.name, selected: inc };
      });
    });
  });

  useEffect(() => {
    if (!selectedMacroAnchors || !anchorMembers) return;
    const newState = mapValues(anchorMembers, (v, dim) => {
      return v.map((m) => {
        const inc = selectedMacroAnchors[dim]?.some((id) => m.id === id);
        return { id: m.id, name: m.name, selected: inc };
      });
    });
    setDimensionState(newState);
  }, [anchorMembers, currentAnchors, selectedMacroAnchors]);

  // split the metrics in half and render in rows
  const top = metrics.slice(0, metrics.length / 2);
  const bottom = metrics.slice(metrics.length / 2);

  // Check if metrics have null values, This is to show loading for macros within review-plans views
  // to prevent the macros from rendering & re-rendering when the data comes through
  const metricsHaveNullValues = top.some((obj) => Object.values(obj).some((value) => value === null));

  const handleDimensionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const dimension = event.target.title;
      const value = event.target.value;
      if (!selectedMacroAnchors) return;

      setSelcetedMacroAnchors({
        ...selectedMacroAnchors,
        [dimension]: [value],
      });
    },
    [selectedMacroAnchors, setSelcetedMacroAnchors]
  );

  if (reallyShowMinimizableItem) {
    return (
      <MinimizableItem className="macro-summaries" title="Plan Summary" style={{ flex: 'none' }}>
        <div className="summary-checkbox">
          {map(dimensionKeys, (dimension) =>
            dimensions[dimension].length > 1 && dimensionState ? (
              <section key={dimension} className="summary-dimensions">
                <div style={{ marginRight: '1rem', fontWeight: 'bold' }}>{capitalize(dimension)}</div>
                {map(dimensionState[dimension], (member) => (
                  <FormControlLabel
                    key={member.id}
                    className="summary-dimension-item"
                    label={member.name}
                    control={
                      <Radio
                        className="summary-dimension-selector"
                        id={member.id}
                        inputProps={{ title: dimension }}
                        value={member.id}
                        checked={member.selected}
                        onChange={handleDimensionChange}
                        color="primary"
                      />
                    }
                  />
                ))}
              </section>
            ) : null
          )}
        </div>

        <Grid container spacing={2} className="macro-summaries-grid">
          <Grid item container spacing={2}>
            {top.map((mv, index) => (
              <Grid item xs key={index} className="summary-column">
                <MacroSummary {...mv} />
              </Grid>
            ))}
          </Grid>
          <Grid item container spacing={2}>
            {bottom.map((mv, index) => (
              <Grid item xs key={index} className="summary-column">
                <MacroSummary {...mv} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </MinimizableItem>
    );
  }

  return (
    <div className="macro-summaries" style={{ flex: 'none' }}>
      {metricsHaveNullValues && <Overlay visible={true} type="loading" />}
      <Grid container spacing={2} className="macro-summaries-grid">
        <Grid item container spacing={2}>
          {top.map((mv, index) => (
            <Grid item xs key={index} className="summary-column">
              <MacroSummary {...mv} />
            </Grid>
          ))}
        </Grid>
        <Grid item container spacing={2}>
          {bottom.map((mv, index) => (
            <Grid item xs key={index} className="summary-column">
              <MacroSummary {...mv} />
            </Grid>
          ))}
        </Grid>
      </Grid>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MacroSummaries);
